<template>
   <LazyLoadingImage class="bg-grey-10">
      <picture v-if="image" class="absolute left-1/2 top-1/2 h-full w-full -translate-x-1/2 -translate-y-1/2">
         <source media="(max-width: 599px)" :srcset="xsAndLgSrcSet" />
         <source media="(min-width: 600px) and (max-width: 991px)" :srcset="smAndMdSrcSet" />
         <source media="(min-width: 992px)" :srcset="xsAndLgSrcSet" />
         <img class="min-h-full min-w-full object-cover" :src="fallbackSrc" :alt="props.image.altTag || ''" />
      </picture>
   </LazyLoadingImage>
</template>

<script setup lang="ts">
export interface ImageCard {
   type: "card_image";
   image: {
      url: string;
      altTag: string;
   };
}

const props = defineProps<ImageCard>();

// =================================================================================================
// ! This component relies on the image URL being a Cloudinary URL.
//
// Responsive image layout:
// Card proportions vary significantly by breakpoint.
// - XL (>=1280px): 576 x 640
// - LG (>=992px): 432 x 576
// - MD (>=768px): 720 x 506
// - SM (>=576px): up to 735 x 560
// - XS (<576px): up to 468 x 560
//
// These are simplified into two aspect ratios for srcset logic:
// - Portrait (576 x 640): used for XL, LG, XS
// - Landscape (735 x 560): used for SM, MD

// Because our future solution for images should involve the admin user uploading one large image,
// saving a portrait and landscape crop, and then auto-resizing, I have included both 1x and 2x resolutions
// in the srcsets for each image ratio, as this will need no extra effort from the admin user

// All files (SVGs and raster images) are treated the same: use 'c_fill,g_faces' to crop and center on faces.
// https://cloudinary.com/documentation/resizing_and_cropping#resize_and_crop_modes
const baseTransform = "c_fill,g_faces,f_auto";

// Split the Cloudinary URL and strip any existing transformations
// This ensures we fully control and override the transformation string
const imageSplit = computed(() => {
   const splitBy = "/image/upload/";
   const [beforeUpload, afterUpload] = props.image.url.split(splitBy);

   // Remove existing transformations if present (strip everything before first 'v123...')
   const afterParts = afterUpload.split("/");
   const versionIndex = afterParts.findIndex((part) => /^v\d+/.test(part));
   const remainingPath = versionIndex !== -1 ? afterParts.slice(versionIndex).join("/") : afterUpload;

   return {
      part1: beforeUpload,
      part2: splitBy,
      part3: remainingPath,
   };
});

const fallbackSrc = computed(() => {
   return `${imageSplit.value.part1}${imageSplit.value.part2}${baseTransform},w_1152,h_1280/${imageSplit.value.part3}`;
});

const xsAndLgSrcSet = computed(() => {
   // supply both 1x and 2x resolutions
   const portraity1res = `${imageSplit.value.part1}${imageSplit.value.part2}${baseTransform},w_576,h_640/${imageSplit.value.part3}`;
   const portraity2res = `${imageSplit.value.part1}${imageSplit.value.part2}${baseTransform},w_1152,h_1280/${imageSplit.value.part3}`;
   return `${portraity1res} 576w, ${portraity2res} 1152w`;
});

const smAndMdSrcSet = computed(() => {
   // supply both 1x and 2x resolutions
   const landscapey1res = `${imageSplit.value.part1}${imageSplit.value.part2}${baseTransform},w_735,h_560/${imageSplit.value.part3}`;
   const landscapey2res = `${imageSplit.value.part1}${imageSplit.value.part2}${baseTransform},w_1470,h_1120/${imageSplit.value.part3}`;
   return `${landscapey1res} 735w, ${landscapey2res} 1470w`;
});
</script>

<style scoped lang="scss"></style>
